import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, User } from 'lucide-react';
import { blogPosts } from '../data/mockData';
import SEO from './SEO';
import { blogPostSchema, breadcrumbSchema } from '../utils/structuredData';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.id === id);

  if (!post) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <SEO 
          title="Post Not Found | Infinite Reyn Productions"
          noindex={true}
        />
        <div className="text-center">
          <h2 className="text-2xl mb-4">Post not found</h2>
          <button
            onClick={() => navigate('/blog')}
            className="text-gold hover:text-gold/80 transition-colors"
          >
            Return to Blog
          </button>
        </div>
      </div>
    );
  }

  const breadcrumbs = breadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' },
    { name: post.title, url: `/blog/${post.id}` }
  ]);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [blogPostSchema(post), breadcrumbs]
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title={`${post.title} | Infinite Reyn Productions`}
        description={post.excerpt}
        canonical={`/blog/${post.id}`}
        ogImage={post.image}
        ogType="article"
        keywords={`${post.category}, ${post.author}, Infinite Reyn Productions, ${post.title}`}
        author={post.author}
        structuredData={combinedSchema}
      />
      {/* Back Button */}
      <button
        onClick={() => navigate('/blog')}
        className="fixed top-8 left-8 z-50 flex items-center gap-2 text-white/80 hover:text-white transition-colors group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        <span className="text-sm tracking-wider">BACK TO BLOG</span>
      </button>

      {/* Featured Image */}
      <div className="relative h-[60vh] overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
        
        {/* Category Badge */}
        <div className="absolute top-8 right-8 border border-gold/50 bg-black/50 backdrop-blur-sm px-4 py-2 text-xs font-light tracking-widest text-gold uppercase">
          {post.category}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-8 -mt-32 relative z-10 pb-24">
        {/* Article Header */}
        <article>
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-light mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-white/50 mb-8">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
            </div>

            {/* Gold Divider */}
            <div className="w-24 h-px bg-gold mb-12"></div>
          </header>

          {/* Article Content */}
          <div 
            className="prose prose-invert prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: post.content }}
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 300,
              lineHeight: 1.8
            }}
          />
        </article>

        {/* Related Posts */}
        <div className="mt-24 pt-20 border-t border-white/10">
          <h3 className="text-xs tracking-[0.3em] text-gold mb-8">MORE FROM THE JOURNAL</h3>
          <div className="grid md:grid-cols-2 gap-8">
            {blogPosts
              .filter(p => p.id !== post.id)
              .slice(0, 2)
              .map(relatedPost => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.id}`}
                  className="group"
                >
                  <div className="relative overflow-hidden aspect-video bg-zinc-900 mb-4">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <p className="text-xs tracking-[0.2em] text-gold mb-2 uppercase">
                    {relatedPost.category}
                  </p>
                  <h4 className="text-xl font-light text-white group-hover:text-gold transition-colors mb-2">
                    {relatedPost.title}
                  </h4>
                  <p className="text-sm text-white/50">
                    {relatedPost.readTime}
                  </p>
                </Link>
              ))}
          </div>
        </div>
      </div>

      {/* Custom Prose Styles */}
      <style>{`
        .prose p {
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 1.5rem;
        }
        .prose h3 {
          color: rgba(255, 255, 255, 0.95);
          font-family: 'Playfair Display', serif;
          font-weight: 300;
          font-size: 1.75rem;
          margin-top: 3rem;
          margin-bottom: 1.5rem;
        }
        .prose ul, .prose ol {
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 1.5rem;
        }
        .prose li {
          margin-bottom: 0.75rem;
        }
        .prose strong {
          color: rgba(255, 255, 255, 0.95);
          font-weight: 500;
        }
        .prose a {
          color: #D4AF37;
          text-decoration: none;
          transition: color 0.2s;
        }
        .prose a:hover {
          color: rgba(212, 175, 55, 0.8);
        }
      `}</style>
    </div>
  );
};

export default BlogPost;